import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { useUser } from '@/hooks/use-user';
import { connectCallsWs } from '@/lib/calls-ws';
import {
  joinJanusVideoRoom,
  requestCallMedia,
  type JanusSessionHandles,
} from '@/lib/janus-videoroom';
import { CallOverlay } from '@/components/calls/call-overlay';
import { toast } from 'sonner';
import {
  CallContext,
  type CallContextValue,
  type CallPeer,
  type CallPhase,
  type CallRecord,
} from '@/providers/call-context';

function errorMessage(err: unknown, fallback: string) {
  if (typeof err === 'object' && err && 'response' in err) {
    const data = (err as { response?: { data?: { error?: string } } }).response?.data;
    if (data?.error) return data.error;
  }
  if (err instanceof DOMException || (err instanceof Error && err.name)) {
    const name = err.name;
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      return 'No camera or microphone found. Check that a device is connected and allowed.';
    }
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      return 'Camera/microphone permission denied. Allow access for this site and try again.';
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
      return 'Camera/microphone is busy in another app. Close it and try again.';
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function withJanusWsOverride(record: CallRecord): CallRecord {
  if (!record.janus) return record;
  const override = import.meta.env.VITE_JANUS_WS_URL?.trim();
  if (!override) return record;
  return { ...record, janus: { ...record.janus, janusWsUrl: override } };
}

export function CallProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState<CallPhase>('idle');
  const [call, setCall] = useState<CallRecord | null>(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const janusRef = useRef<JanusSessionHandles | null>(null);
  const ringingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callIdRef = useRef<string | null>(null);

  const clearRingingTimer = () => {
    if (ringingTimerRef.current) clearTimeout(ringingTimerRef.current);
    ringingTimerRef.current = null;
  };

  const teardownMedia = useCallback(() => {
    janusRef.current?.stop();
    janusRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setMuted(false);
    setCameraOff(false);
  }, []);

  const resetCall = useCallback(() => {
    clearRingingTimer();
    teardownMedia();
    callIdRef.current = null;
    setCall(null);
    setPhase('idle');
  }, [teardownMedia]);

  const attachMedia = useCallback(
    async (record: CallRecord, existingLocalStream?: MediaStream) => {
      if (!record.janus) throw new Error('Missing Janus credentials');
      janusRef.current?.stop();
      janusRef.current = null;
      setRemoteStream(null);
      if (existingLocalStream) {
        setLocalStream(existingLocalStream);
      }
      const session = await joinJanusVideoRoom(
        {
          wsUrl: record.janus.janusWsUrl,
          apiSecret: record.janus.janusApiSecret,
          roomId: record.janus.roomId,
          pin: record.janus.pin,
          iceServers: record.janus.iceServers,
          displayName: user
            ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.id
            : 'Milora',
        },
        (stream) => setRemoteStream(stream),
        existingLocalStream
      );
      janusRef.current = session;
      setLocalStream(session.localStream);
    },
    [user]
  );

  const scheduleRingingTimeout = useCallback(
    (record: CallRecord) => {
      clearRingingTimer();
      const created = new Date(record.createdAt).getTime();
      const remaining = Math.max(0, (record.ringingTimeoutMs || 45000) - (Date.now() - created));
      ringingTimerRef.current = setTimeout(() => {
        if (callIdRef.current !== record.id) return;
        void axiosInstance.post(`/api/calls/${record.id}/end`).catch(() => undefined);
        toast.message('Call missed');
        resetCall();
      }, remaining + 250);
    },
    [resetCall]
  );

  const startCall = useCallback(
    async (calleeId: string) => {
      if (phase !== 'idle') {
        toast.error('You are already in a call');
        return;
      }
      let createdId: string | null = null;
      let media: MediaStream | null = null;
      try {
        // Request permissions in the same turn as the Call click (browser gesture).
        media = await requestCallMedia();
        setLocalStream(media);
        setPhase('outgoing');
        const { data } = await axiosInstance.post<CallRecord>('/api/calls', { calleeId });
        createdId = data.id;
        callIdRef.current = data.id;
        const next = withJanusWsOverride(data);
        setCall(next);
        scheduleRingingTimeout(next);
        await attachMedia(next, media);
        media = null;
      } catch (err) {
        media?.getTracks().forEach((track) => track.stop());
        if (createdId) {
          void axiosInstance.post(`/api/calls/${createdId}/end`).catch(() => undefined);
        }
        resetCall();
        toast.error(errorMessage(err, 'Could not start call'));
      }
    },
    [attachMedia, phase, resetCall, scheduleRingingTimeout]
  );

  const acceptCall = useCallback(async () => {
    if (!call) return;
    const callId = call.id;
    let media: MediaStream | null = null;
    try {
      // Request permissions in the same turn as the Accept click (browser gesture).
      media = await requestCallMedia();
      setLocalStream(media);
      const { data } = await axiosInstance.post<CallRecord>(`/api/calls/${callId}/accept`);
      callIdRef.current = data.id;
      const next = withJanusWsOverride(data);
      setCall(next);
      setPhase('active');
      clearRingingTimer();
      await attachMedia(next, media);
      media = null;
    } catch (err) {
      media?.getTracks().forEach((track) => track.stop());
      void axiosInstance.post(`/api/calls/${callId}/end`).catch(() => undefined);
      toast.error(errorMessage(err, 'Could not accept call'));
      resetCall();
    }
  }, [attachMedia, call, resetCall]);

  const rejectCall = useCallback(async () => {
    if (!call) return;
    try {
      await axiosInstance.post(`/api/calls/${call.id}/reject`);
    } catch {
      /* ignore */
    }
    resetCall();
  }, [call, resetCall]);

  const endCall = useCallback(async () => {
    if (!call) {
      resetCall();
      return;
    }
    try {
      await axiosInstance.post(`/api/calls/${call.id}/end`);
    } catch {
      /* ignore */
    }
    resetCall();
  }, [call, resetCall]);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      localStream?.getAudioTracks().forEach((t) => {
        t.enabled = !next;
      });
      return next;
    });
  }, [localStream]);

  const toggleCamera = useCallback(() => {
    setCameraOff((prev) => {
      const next = !prev;
      localStream?.getVideoTracks().forEach((t) => {
        t.enabled = !next;
      });
      return next;
    });
  }, [localStream]);

  useEffect(() => {
    if (!user?.id) return;

    const conn = connectCallsWs((message) => {
      const type = String(message.type || '');
      if (type === 'call.invite') {
        const callId = String(message.callId || '');
        if (!callId || callIdRef.current) return;
        if (String(message.calleeId) !== user.id) return;
        const incoming: CallRecord = {
          id: callId,
          callerId: String(message.callerId || ''),
          calleeId: String(message.calleeId || ''),
          status: 'ringing',
          ringingTimeoutMs: Number(message.ringingTimeoutMs) || 45000,
          createdAt: String(message.createdAt || new Date().toISOString()),
          peer: (message.caller as CallPeer) || null,
          janus: null,
        };
        callIdRef.current = callId;
        setCall(incoming);
        setPhase('incoming');
        scheduleRingingTimeout(incoming);
        return;
      }

      if (type === 'call.accepted') {
        const callId = String(message.callId || '');
        if (callId && callId === callIdRef.current) {
          clearRingingTimer();
          setPhase('active');
          setCall((prev) => (prev ? { ...prev, status: 'active' } : prev));
        }
        return;
      }

      if (type === 'call.ended') {
        const callId = String(message.callId || '');
        if (callId && callId === callIdRef.current) {
          const status = String(message.status || 'ended');
          if (status === 'rejected') toast.message('Call declined');
          else if (status === 'missed') toast.message('Call missed');
          else toast.message('Call ended');
          resetCall();
        }
      }
    });

    return () => {
      conn.close();
      resetCall();
    };
  }, [resetCall, scheduleRingingTimeout, user?.id]);

  useEffect(() => {
    const match = location.pathname.match(/^\/calls\/([^/]+)/);
    if (!match || !user?.id) return;
    const callId = match[1];
    if (callIdRef.current === callId) return;

    let cancelled = false;
    void (async () => {
      try {
        const { data } = await axiosInstance.get<CallRecord>(`/api/calls/${callId}`);
        if (cancelled) return;
        if (data.status !== 'ringing' && data.status !== 'active') {
          navigate('/', { replace: true });
          return;
        }
        callIdRef.current = data.id;
        setCall(data);
        if (data.status === 'ringing' && data.calleeId === user.id) {
          setPhase('incoming');
          scheduleRingingTimeout(data);
        } else if (data.status === 'active') {
          setPhase('active');
          await attachMedia(data);
        } else if (data.status === 'ringing' && data.callerId === user.id) {
          setPhase('outgoing');
          scheduleRingingTimeout(data);
          await attachMedia(data);
        }
      } catch {
        if (!cancelled) navigate('/', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attachMedia, location.pathname, navigate, scheduleRingingTimeout, user?.id]);

  const value = useMemo<CallContextValue>(
    () => ({
      phase,
      call,
      startCall,
      acceptCall,
      rejectCall,
      endCall,
      muted,
      cameraOff,
      toggleMute,
      toggleCamera,
      localStream,
      remoteStream,
    }),
    [
      acceptCall,
      call,
      cameraOff,
      endCall,
      localStream,
      muted,
      phase,
      rejectCall,
      remoteStream,
      startCall,
      toggleCamera,
      toggleMute,
    ]
  );

  return (
    <CallContext.Provider value={value}>
      {children}
      <CallOverlay />
    </CallContext.Provider>
  );
}
